/**
 * Where to send someone after sign-in / sign-up / an auth callback.
 *
 * Only an app-relative path is accepted: it starts with exactly one `/`,
 * has no backslash or control character, and resolves to the same origin.
 * Anything else — an absolute URL, `//host`, `/\host`, `javascript:` —
 * falls back. Every `?next=` / `returnTo` reader goes through this: until
 * 2026-10-21 the login and signup pages assigned the raw value to
 * `window.location.href`, so a crafted link ran script on the app origin
 * after a password sign-in, and the OAuth callback built
 * `${origin}${next}` (`next=@evil.com` → another host).
 */
export function safeNextPath(next: string | null | undefined): string | null
export function safeNextPath(next: string | null | undefined, fallback: string): string
export function safeNextPath(next: string | null | undefined, fallback: string | null = null): string | null {
  if (typeof next !== 'string' || next.length === 0 || next.length > 2048) return fallback
  if (next[0] !== '/' || next[1] === '/' || next[1] === '\\') return fallback
  // Backslashes are read as slashes by browsers; control characters can
  // smuggle a scheme past naive checks. Neither belongs in a real path.
  if (/[\\\u0000-\u001f\u007f]/.test(next)) return fallback
  const base = 'https://app.invalid'
  try {
    const url = new URL(next, base)
    if (url.origin !== base) return fallback
    return url.pathname + url.search + url.hash
  } catch {
    return fallback
  }
}
